import { Grid, Tooltip } from '@mui/material';
import React, { useCallback, useContext, useMemo } from 'react';
import type { KeyValue, Reference } from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../../common/auth/UserContext';
import { useIsWithinBreakpoints } from '../../common/hooks/useIsWithinBreakpoints';
import { useI18n } from '../../common/i18n/I18n';
import { Icons } from '../../common/icons/Icons';
import { H, V } from '../../common/Layout';
import useHistory from '../../common/utils/useHistory';
import AkaTile from '../header/AkaLabel';
import { OpenDetailsInNewTabLink } from '../header/OpenDetailsInNewTabLink';
import { ProductionStatusChips } from '../header/ProductionStatusChips';
import RoundAvatar, { AvatarSizes } from '../header/RoundAvatar';
import TitleLabel from '../header/TitleLabel';
import type { ProductionTypeSuperset } from '../helpers/productionApi';
import { useDataListCell } from './DataListCellContext';

type ProductionDetailsSectionProps = {
    id: string;
    name: string;
    otherTitles?: KeyValue[];
    showOpenInNewTabLink?: boolean;
    productionType?: ProductionTypeSuperset;
    createdFrom?: Reference;
};

type ProductionStatusSectionProps = {
    productionType?: ProductionTypeSuperset;
};

function ProductionStatusSection({ productionType }: ProductionStatusSectionProps) {
    const isSmallDevice = useIsWithinBreakpoints(['xs', 's']);
    const { isNotesPanelOpen } = useDataListCell();

    if (isSmallDevice || isNotesPanelOpen) {
        return null;
    }

    return (
        <H sx={{ width: 'fit-content', flexShrink: 0 }}>
            <ProductionStatusChips
                productionStatus={productionType?.productionStatus}
                projectStatus={productionType?.projectStatus}
            />
        </H>
    );
}

export default function ProductionDetailsSection({
    id,
    name,
    otherTitles,
    showOpenInNewTabLink,
    productionType,
    createdFrom
}: ProductionDetailsSectionProps) {
    const { application } = useContext(UserContext);
    const { changeRoute, location } = useHistory();
    const t = useI18n();
    const pathname = location?.pathname || '';
    const isInPeople = /\/people(\/|$)/.test(pathname);
    const Container = isInPeople ? V : H;
    const enableOpenProductionOnTitleClick = showOpenInNewTabLink;

    const handleOnClickTitle = useCallback(() => {
        changeRoute(`${application.home}/workspace/${id}/details`);
    }, [application.home, changeRoute, id]);

    const titleOnClick = useMemo(
        () => (enableOpenProductionOnTitleClick ? handleOnClickTitle : undefined),
        [enableOpenProductionOnTitleClick, handleOnClickTitle]
    );

    const akaOnClick = useMemo(
        () => (showOpenInNewTabLink ? handleOnClickTitle : undefined),
        [showOpenInNewTabLink, handleOnClickTitle]
    );

    return (
        <Container gap={1}>
            <H gap={1}>
                <RoundAvatar title={name} size={AvatarSizes.md} />
                <Grid container spacing={0.5} sx={{ alignItems: 'baseline' }}>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <H sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                            <H sx={{ flex: 1, minWidth: 0 }}>
                                <TitleLabel title={name} onClick={titleOnClick} />
                            </H>
                            {!isInPeople && <ProductionStatusSection productionType={productionType} />}
                        </H>
                    </Grid>

                    <Grid size={12} sx={{ display: 'flex', gap: 0.5 }}>
                        {showOpenInNewTabLink && createdFrom && !createdFrom?.deleted && (
                            <Tooltip title={t('pipeline.view.submission')}>
                                <Icons.SubmissionsPipeline
                                    sx={{
                                        height: 18,
                                        width: 18,
                                        color: 'primary.main'
                                    }}
                                    onClick={() => {
                                        if (createdFrom?.id) {
                                            changeRoute(`${application.home}/pipeline`, {
                                                submissionId: createdFrom.id
                                            });
                                        }
                                    }}
                                />
                            </Tooltip>
                        )}
                        {showOpenInNewTabLink && <OpenDetailsInNewTabLink workspaceId={id} />}
                        <AkaTile otherTitles={otherTitles} onClick={akaOnClick} />
                    </Grid>
                </Grid>
            </H>
            {isInPeople && <ProductionStatusSection productionType={productionType} />}
        </Container>
    );
}
